import { useEffect, useRef } from 'react';
import { Box, Typography, Dialog, IconButton, Stack, CircularProgress } from '@mui/material';
import StopIcon from '@mui/icons-material/Stop';
import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic';
import { glass } from '../themes';
import type { VoiceAgentState } from '../hooks/useVoiceAgent';

interface Props {
  open: boolean;
  transcript: string;
  partial: string;
  agentState: VoiceAgentState;
  isRecording: boolean;
  onStop: () => void;
  onDismiss: () => void;
  onFollowUp: () => void;
}

export default function RecordingModal({ open, transcript, partial, agentState, isRecording, onStop, onDismiss, onFollowUp }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript, partial, agentState.fullResponse]);

  const displayText = transcript + (partial ? (transcript ? ' ' : '') + partial : '');
  // Show recording UI when idle (includes the brief moment before isRecording turns true)
  const showRecordingUI = agentState.status === 'idle';
  const isProcessing = agentState.status === 'thinking' || agentState.status === 'responding';
  const isDone = agentState.status === 'done' || agentState.status === 'error';

  return (
    <Dialog
      open={open}
      onClose={isDone ? onDismiss : undefined}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '24px',
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.7)',
          boxShadow: '0 8px 40px rgba(30,58,95,0.15)',
          m: 2,
        },
      }}
    >
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* ── Header ── */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap={1.5}>
            {isRecording && (
              <Box sx={{
                width: 10, height: 10, borderRadius: '50%', bgcolor: 'error.main',
                animation: 'recpulse 1s ease-in-out infinite',
                '@keyframes recpulse': {
                  '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                  '50%': { opacity: 0.4, transform: 'scale(0.8)' },
                },
              }} />
            )}
            {isProcessing && (
              <CircularProgress size={16} thickness={5} sx={{ color: '#1e3a5f' }} />
            )}
            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: glass.text }}>
              {showRecordingUI
                ? 'Recording…'
                : agentState.status === 'thinking' ? 'Thinking…'
                : agentState.status === 'responding' ? 'Responding…'
                : agentState.status === 'error' ? 'Error'
                : 'Done'}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" gap={1}>
            {/* Follow-up mic — show when done */}
            {isDone && (
              <IconButton
                onClick={onFollowUp}
                sx={{
                  bgcolor: '#1e3a5f', color: '#fff',
                  width: 40, height: 40,
                  '&:hover': { bgcolor: '#162d4a' },
                  boxShadow: '0 2px 10px rgba(30,58,95,0.3)',
                }}
              >
                <MicIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
            {/* Stop button — show while in recording UI */}
            {showRecordingUI && (
              <IconButton
                onClick={onStop}
                sx={{
                  bgcolor: 'error.main', color: '#fff',
                  width: 44, height: 44,
                  '&:hover': { bgcolor: 'error.dark' },
                  boxShadow: '0 2px 10px rgba(211,47,47,0.35)',
                }}
              >
                <StopIcon sx={{ fontSize: 22 }} />
              </IconButton>
            )}
            {/* Close — show when done */}
            {isDone && (
              <IconButton onClick={onDismiss} size="small">
                <CloseIcon sx={{ fontSize: 18, color: 'rgba(0,0,0,0.4)' }} />
              </IconButton>
            )}
          </Stack>
        </Stack>

        {/* ── Body ── */}
        <Box
          ref={scrollRef}
          sx={{
            minHeight: 120, maxHeight: 320,
            overflow: 'auto',
            bgcolor: 'rgba(30,58,95,0.04)',
            borderRadius: '14px',
            p: 2,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {/* Recording UI: live transcript */}
          {showRecordingUI && (
            displayText ? (
              <Typography sx={{ fontSize: '1rem', lineHeight: 1.7, color: glass.text, whiteSpace: 'pre-wrap' }}>
                {transcript}
                {partial && (
                  <Box component="span" sx={{ color: glass.textSecondary }}>
                    {transcript ? ' ' : ''}{partial}
                  </Box>
                )}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: '0.9rem', color: 'rgba(0,0,0,0.3)', fontStyle: 'italic', textAlign: 'center', mt: 4 }}>
                Start speaking…
              </Typography>
            )
          )}

          {/* While thinking (no response yet): dots */}
          {!showRecordingUI && agentState.status === 'thinking' && !agentState.fullResponse && (
            <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5 }}>
              {[0, 1, 2].map(i => (
                <Box key={i} sx={{
                  width: 6, height: 6, borderRadius: '50%', bgcolor: '#1e3a5f',
                  opacity: 0.4,
                  animation: 'dotpulse 1.2s ease-in-out infinite',
                  animationDelay: `${i * 0.2}s`,
                  '@keyframes dotpulse': { '0%, 100%': { opacity: 0.2 }, '50%': { opacity: 0.8 } },
                }} />
              ))}
            </Box>
          )}

          {/* Agent response text */}
          {!showRecordingUI && agentState.status === 'error' && (
            <Typography sx={{ fontSize: '0.9rem', color: 'rgba(180,0,0,0.8)' }}>
              {agentState.error}
            </Typography>
          )}
          {!showRecordingUI && agentState.fullResponse && (
            <Typography sx={{ fontSize: '0.9rem', color: glass.text, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
              {agentState.fullResponse}
            </Typography>
          )}
        </Box>

        {/* Footer hint */}
        {showRecordingUI && (
          <Typography sx={{ fontSize: '0.75rem', color: glass.textSecondary, textAlign: 'center' }}>
            Stops automatically when you finish speaking
          </Typography>
        )}
        {isDone && (
          <Typography sx={{ fontSize: '0.75rem', color: glass.textSecondary, textAlign: 'center' }}>
            Tap the mic to follow up
          </Typography>
        )}
      </Box>
    </Dialog>
  );
}
